// dashboard/printSummary.js
// Builds the printable/exportable patient summary HTML (PDF via browser
// print). Extracted from App.js (2026-08-12 split) — no React, just
// string-building functions called from handlePrintSummary in App.js.

import i18n from '../i18n';
import { CAIRN_GLYPH_PATHS, conditionLabel, severityLabel, REASON_CODE_CLINIC_LABEL, EVENT_TYPE_KEYS } from './constants';
import { calcPrintAge, KG_TO_LBS, trendDirectionLabel } from './utils';
import { buildFindingsNarrative } from './buildFindingsNarrative';

// Escapes owner/pet/clinic-entered free text before it's interpolated into
// this template. pet.name, pet.species, pet.breed, owner names, and
// medication_name are all free text collected from the mobile app with no
// HTML sanitization on write. handlePrintSummary (App.js) opens this HTML
// via window.open('') + document.write(), which shares the dashboard's own
// origin — unescaped user text here is a stored-XSS path straight into a
// logged-in clinician's session (localStorage, where the Supabase session
// lives). Also needed for values passed as i18n.t() interpolation params,
// not just direct template-literal `${}` spots — i18next's own
// interpolation is deliberately NOT auto-escaping (escapeValue:false in
// i18n.js, so translated strings can contain intentional markup), so it
// does nothing to protect these on its own. Found in the 2026-08-16 audit.
function esc(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildTrendSvg(surveys, svgW, svgH, evaluationBySurveyId, vnextRunBySurveyId) {
  if (!surveys.length) return `<p style="font-size:12px;color:#999;margin:8px 0 0;">${i18n.t('printSummary.noCheckInData')}</p>`;
  const pad = { t: 12, r: 16, b: 28, l: 30 };
  const w = svgW - pad.l - pad.r;
  const h = svgH - pad.t - pad.b;
  const ts  = surveys.map(s => new Date(s.submitted_at).getTime());
  const tMin = ts[0], tRange = (ts[ts.length - 1] - ts[0]) || 1;
  const xP = t  => pad.l + ((t - tMin) / tRange) * w;
  // Auto-scales off the survey set's own actual min/max (matching
  // TrendChart's live-dashboard equivalent) instead of assuming every
  // clinical_score is a 0-100 value — SRR's is a raw breaths/min reading
  // (clinical_score_kind='respiratory_rate', migration 207), and
  // hardcoding a 0-100 axis compressed its whole trend line into a
  // sliver of the chart and, worse, printed a false "/100" next to a bpm
  // reading in this exported document. Now reads clinical_score (server-
  // authoritative, migrated from composite_score in the 2026-09-09 audit)
  // instead of the client-computed value.
  const scores  = surveys.map(s => s.clinical_score).filter(sc => sc != null);
  const sMinRaw = scores.length ? Math.min(...scores) : 0;
  const sMaxRaw = scores.length ? Math.max(...scores) : 100;
  const sPad    = Math.max(1, (sMaxRaw - sMinRaw) * 0.15);
  const sMin    = sMinRaw - sPad;
  const sMax    = sMaxRaw + sPad;
  const sRange  = (sMax - sMin) || 1;
  const yP = sc => pad.t + (1 - ((sc ?? (sMin + sMax) / 2) - sMin) / sRange) * h;
  const pts = surveys.map((s, i) => `${xP(ts[i])},${yP(s.clinical_score)}`).join(' ');
  // Release-blocker #1 (docs/BUILD_STATE.md, 2026-09-27, Bryan): no raw
  // numeric score anywhere client-facing, including this exported PDF.
  // Gridlines stay (shape/position only); their numeric value labels
  // (${v}) are removed — the underlying score still positions the line/
  // dots below, it's just never rendered as text.
  const gridVals = [sMin, (sMin + sMax) / 2, sMax];
  const grids = gridVals.map((v, i) => `
    <line x1="${pad.l}" y1="${yP(v)}" x2="${pad.l+w}" y2="${yP(v)}" stroke="#e0e0e0" stroke-width="1" ${i===1?'stroke-dasharray="3,2"':''}/>`).join('');
  const step = Math.max(1, Math.ceil(surveys.length / 5));
  const xLabels = surveys.filter((_, i) => i % step === 0 || i === surveys.length - 1).map(s => {
    const t = new Date(s.submitted_at).getTime();
    const lbl = new Date(s.submitted_at).toLocaleDateString(i18n.language, { month: 'short', year: '2-digit' });
    return `<text x="${xP(t)}" y="${pad.t+h+18}" text-anchor="middle" font-size="9" fill="#bbb">${lbl}</text>`;
  }).join('');
  const dots = surveys.map((s, i) =>
    `<circle cx="${xP(ts[i])}" cy="${yP(s.clinical_score)}" r="3" fill="white" stroke="#333" stroke-width="1.5"/>`).join('');
  const last = surveys[surveys.length - 1];
  const lastDate  = new Date(last.submitted_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
  // Release-blocker #1: no raw score digits in the exported PDF either --
  // the big number here used to be the literal clinical_score; replaced
  // with the same qualitative trend word used on the live dashboard.
  const lastDirection = last.clinical_score_delta != null ? trendDirectionLabel(last.clinical_score_delta, i18n.t) : null;
  // Release-blocker #2: prefer the vNext plain-English findings narrative
  // over the OLD engine's bare reason-code label, same preference order
  // as the live dashboard's formatEvaluationTooltip.
  const lastRun = vnextRunBySurveyId?.get(last.id);
  const lastFindingsLines = buildFindingsNarrative(lastRun?.findings, lastRun?.clusters);
  const lastEval = evaluationBySurveyId?.get(last.id);
  const lastReasonLabel = lastFindingsLines.length > 0
    ? lastFindingsLines.join(' ')
    : lastEval
      ? (REASON_CODE_CLINIC_LABEL[lastEval.primary_reason_code] || lastEval.primary_reason_code)
      : null;
  return `
    <div style="display:flex;align-items:flex-start;gap:16px">
      <svg width="${svgW}" height="${svgH}" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;overflow:visible">
        <style>text{font-family:Helvetica,Arial,sans-serif}</style>
        ${grids}
        <polyline points="${pts}" fill="none" stroke="#333" stroke-width="2" stroke-linejoin="round"/>
        ${dots}
        ${xLabels}
      </svg>
      <div style="text-align:right;padding-top:8px;flex-shrink:0;max-width:150px">
        ${lastDirection ? `<div style="font-size:18px;font-weight:700;line-height:1">${lastDirection}</div>` : ''}
        <div style="font-size:10px;color:#999;margin-top:3px">${i18n.t('printSummary.latestValue', { date: lastDate })}</div>
        ${lastReasonLabel ? `<div style="font-size:10px;color:#777;margin-top:4px;text-align:right">${lastReasonLabel}</div>` : ''}
      </div>
    </div>`;
}

// Compact weight card: trend arrow + last 3 weights
function buildWeightWidget(weights, useLbs) {
  if (!weights.length) return '';
  const unit   = useLbs ? 'lbs' : 'kg';
  const last3  = weights.slice(-3).reverse(); // newest first for display
  const threeMonthsAgo = new Date(); threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const recent = weights.filter(w => new Date(w.measured_at + 'T00:00:00') >= threeMonthsAgo);
  let arrow = '→', trend = i18n.t('trackers.weight_trend_stable'), pctLabel = '';
  if (recent.length >= 2) {
    const pct = ((parseFloat(recent[recent.length-1].weight) - parseFloat(recent[0].weight)) / parseFloat(recent[0].weight)) * 100;
    if (pct > 5)       { arrow = '↗'; trend = i18n.t('trackers.weight_trend_gaining'); pctLabel = `+${pct.toFixed(1)}% (3 mo)`; }
    else if (pct < -5) { arrow = '↘'; trend = i18n.t('trackers.weight_trend_losing');  pctLabel = `${pct.toFixed(1)}% (3 mo)`;  }
    else               { pctLabel = `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% (3 mo)`; }
  }
  const entries = last3.map((w, i) => {
    const d = new Date(w.measured_at + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    const display = useLbs ? (parseFloat(w.weight) * KG_TO_LBS).toFixed(1) : parseFloat(w.weight).toFixed(1);
    return `<div style="margin-bottom:3px">
      <span style="font-size:${i===0?13:12}px;font-weight:${i===0?600:400};color:${i===0?'#1a1a1a':'#777'}">${display} ${unit}</span>
      <span style="font-size:10px;color:#aaa;margin-left:4px">${d}</span>
    </div>`;
  }).join('');
  return `
    <div style="flex:1;min-width:150px;border:1px solid #e0e0e0;border-radius:8px;padding:14px 16px;break-inside:avoid">
      <div style="font-size:9px;font-weight:700;letter-spacing:1.2px;color:#777;text-transform:uppercase;margin-bottom:10px">${i18n.t('trackers.weight_title')}</div>
      <div style="display:flex;align-items:flex-start;gap:12px">
        <div style="text-align:center;flex-shrink:0;margin-top:2px">
          <div style="font-size:28px;line-height:1;color:#333">${arrow}</div>
          <div style="font-size:9px;color:#777;margin-top:3px;font-family:Helvetica,Arial,sans-serif">${trend}</div>
        </div>
        <div>
          ${entries}
          ${pctLabel ? `<div style="font-size:10px;color:#aaa;margin-top:4px">${pctLabel}</div>` : ''}
        </div>
      </div>
    </div>`;
}

// Compact wellbeing card: monthly counts
function buildWellbeingWidget(goodDays) {
  if (!goodDays.length) return '';
  const byMonth = {};
  goodDays.forEach(e => {
    const key = new Date(e.log_date + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'short', year: '2-digit' });
    if (!byMonth[key]) byMonth[key] = { good: 0, difficult: 0 };
    if (e.value === 'good') byMonth[key].good++;
    else if (e.value === 'difficult') byMonth[key].difficult++;
  });
  const goodTotal = goodDays.filter(e => e.value === 'good').length;
  const diffTotal = goodDays.filter(e => e.value === 'difficult').length;
  const cells = Object.entries(byMonth).map(([mo, c]) =>
    `<td style="padding:5px 8px;text-align:center;border-right:1px solid #eee;vertical-align:top">
      <div style="font-size:9px;color:#aaa;margin-bottom:2px">${mo}</div>
      <div style="font-size:11px;font-weight:600">${c.good}✓</div>
      <div style="font-size:11px;color:#888">${c.difficult}—</div>
    </td>`).join('');
  return `
    <div style="flex:2;min-width:200px;border:1px solid #e0e0e0;border-radius:8px;padding:14px 16px;break-inside:avoid">
      <div style="display:flex;align-items:baseline;gap:10px;margin-bottom:10px">
        <div style="font-size:9px;font-weight:700;letter-spacing:1.2px;color:#777;text-transform:uppercase">${i18n.t('printSummary.wellbeingLog')}</div>
        <div style="font-size:10px;color:#aaa">${i18n.t('printSummary.goodDifficultCount', { good: goodTotal, difficult: diffTotal })}</div>
      </div>
      <div style="overflow-x:auto"><table style="border-collapse:collapse;font-family:Helvetica,Arial,sans-serif"><tr>${cells}</tr></table></div>
    </div>`;
}

// Compact seizure card: most recent date + six-month average interval.
function buildSeizureWidget(seizures) {
  if (!seizures.length) return '';
  // seizures already sorted newest-first from query
  const latestDate = new Date(seizures[0].occurred_at)
    .toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
  const sixMonthsAgo = new Date(); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const last6m = [...seizures].filter(s => new Date(s.occurred_at) >= sixMonthsAgo)
    .sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
  let avgLine = '';
  if (last6m.length >= 2) {
    let total = 0;
    for (let i = 1; i < last6m.length; i++)
      total += (new Date(last6m[i].occurred_at) - new Date(last6m[i-1].occurred_at)) / 86400000;
    const avg = Math.round(total / (last6m.length - 1));
    avgLine = `<div style="margin-top:10px;padding-top:10px;border-top:1px solid #eee;font-size:11px;color:#555">
      <span style="font-size:9px;color:#aaa;display:block;margin-bottom:2px;text-transform:uppercase;letter-spacing:0.5px">${i18n.t('printSummary.avgIntervalLabel')}</span>
      ${i18n.t('printSummary.days', { count: avg })}
    </div>`;
  } else {
    avgLine = `<div style="margin-top:8px;font-size:10px;color:#aaa">${last6m.length === 1 ? i18n.t('printSummary.oneEventLast6mo') : i18n.t('printSummary.noEventsLast6mo')}</div>`;
  }
  return `
    <div style="flex:1;min-width:160px;border:1px solid #e0e0e0;border-radius:8px;padding:14px 16px;break-inside:avoid">
      <div style="font-size:9px;font-weight:700;letter-spacing:1.2px;color:#777;text-transform:uppercase;margin-bottom:10px">${i18n.t('printSummary.seizureLog')}</div>
      <div style="font-size:9px;color:#aaa;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px">${i18n.t('printSummary.lastSeizureDate')}</div>
      <div style="font-size:12px;color:#333;margin-bottom:2px">${latestDate}</div>
      ${avgLine}
    </div>`;
}

function buildEventsWidget(events) {
  if (!events.length) return '';
  // Reuses eventType.* — same label map PatientDetailPanel's own
  // recent-events list uses, so this print export can't drift from what
  // the live dashboard shows for the same event.
  const label = (eventType) => EVENT_TYPE_KEYS.has(eventType)
    ? i18n.t(`eventType.${eventType}`)
    : esc((eventType || i18n.t('eventType.fallback')).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  const rows = events.slice(0, 5).map(ev => {
    const d = new Date(ev.event_date + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    return `<div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:4px">
      <span style="color:#333">${label(ev.event_type)}</span>
      <span style="color:#aaa">${d}</span>
    </div>`;
  }).join('');
  return `
    <div style="flex:1;min-width:160px;border:1px solid #e0e0e0;border-radius:8px;padding:14px 16px;break-inside:avoid">
      <div style="font-size:9px;font-weight:700;letter-spacing:1.2px;color:#777;text-transform:uppercase;margin-bottom:10px">${i18n.t('patientDetail.recentEvents')}</div>
      ${rows}
    </div>`;
}

export function buildPrintHtml({ pet, owner, conditions, condSeverity, condSurveys, evaluationBySurveyId, vnextRunBySurveyId, medications, adherenceCounts, weights, goodDays, seizures, events, useLbs }) {
  const GLYPH = (sz, fills) => `
    <svg width="${sz}" height="${sz}" viewBox="0 0 128 128" style="flex-shrink:0">
      ${CAIRN_GLYPH_PATHS.map((d, i) => `<path d="${d}" fill="${fills[i]}"/>`).join('\n      ')}
    </svg>`;
  const GLYPH_LG = GLYPH(32, ['#aaa','#999','#888','#777','#666']);
  const GLYPH_SM = GLYPH(18, ['#bbb','#aaa','#999','#888','#777']);

  const generatedDate = new Date().toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' });
  const petAge = calcPrintAge(pet.date_of_birth);
  const dobStr = pet.date_of_birth
    ? new Date(pet.date_of_birth + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  // Escaped once here — see esc()'s own comment for why every interpolation
  // below needs this, including inside i18n.t() params.
  const petNameSafe = esc(pet.name);
  const petIdentityLineSafe = esc([pet.species ? pet.species.charAt(0).toUpperCase() + pet.species.slice(1) : null, pet.breed].filter(Boolean).join(' · '));
  const ownerNameSafe = owner ? esc(`${owner.first_name} ${owner.last_name}`) : '';

  // ── Medications list (inline in patient card) ────────────────────────────────
  // adherenceCounts (adherence_entries — only ever written from the mobile
  // app's "Mark as Given" push action, previously collected but never read
  // anywhere at all) shows as a "confirmed given" count over this same
  // print window when present, blank otherwise (most medications won't
  // have any, since it depends on the owner tapping that specific
  // notification action rather than logging in-app).
  const medLines = medications.length
    ? medications.map(m => {
        const dose = esc([m.dose, m.dose_unit].filter(Boolean).join(' '));
        const medNameSafe = esc(m.medication_name);
        const freqSafe = esc(m.frequency);
        const confirmedCount = adherenceCounts?.[m.id];
        return `<div style="margin-bottom:6px">
          <span style="font-size:12px;font-weight:600">${medNameSafe}</span>
          ${dose ? `<span style="font-size:11px;color:#777;margin-left:4px">${dose}</span>` : ''}
          ${freqSafe ? `<span style="font-size:11px;color:#aaa;margin-left:4px">· ${freqSafe}</span>` : ''}
          ${confirmedCount ? `<span style="font-size:11px;color:#7A8F6B;margin-left:4px">· ${i18n.t('printSummary.confirmedGiven', { count: confirmedCount })}</span>` : ''}
        </div>`;
      }).join('')
    : `<div style="font-size:12px;color:#bbb">${i18n.t('printSummary.noneTracked')}</div>`;

  // ── Condition sections ───────────────────────────────────────────────────────
  const numCond = conditions.length;
  const condSections = conditions.map((cond, idx) => {
    const surveys = condSurveys[cond.id] || [];
    const label   = conditionLabel(cond.condition_type);
    // Same alert-derived severity + wording as the live dashboard badge —
    // not conditions.status, which can disagree with it (see handlePrintSummary).
    const status  = severityLabel(condSeverity?.[cond.id] || 'green');
    const svgW    = 430;
    const svgH    = numCond === 1 ? 130 : 105;
    const pageBreakAfter = numCond >= 3 && idx === 1 ? 'page-break-after:always;' : '';
    const checkIns = surveys.length;
    return `
      <div style="margin-bottom:${idx < conditions.length - 1 ? 18 : 0}px;${pageBreakAfter}">
        <div style="display:flex;align-items:baseline;gap:10px;margin-bottom:8px">
          <div style="font-size:12px;font-weight:700">${label}</div>
          <div style="font-size:11px;color:#777;font-style:italic">${status}</div>
          <div style="font-size:10px;color:#aaa;margin-left:auto">${i18n.t('printSummary.checkIns', { count: checkIns })}</div>
        </div>
        ${buildTrendSvg(surveys, svgW, svgH, evaluationBySurveyId, vnextRunBySurveyId)}
      </div>`;
  }).join('');

  // ── Optional tracker widgets ─────────────────────────────────────────────────
  const hasTrackers = weights.length || goodDays.length || seizures.length || events.length;
  const trackerRow = hasTrackers ? `
    <div style="display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap">
      ${buildWeightWidget(weights, useLbs)}
      ${buildWellbeingWidget(goodDays)}
      ${buildSeizureWidget(seizures)}
      ${buildEventsWidget(events)}
    </div>` : '';

  return `<!DOCTYPE html>
<html lang="${i18n.language}">
<head>
  <meta charset="utf-8"/>
  <title>${i18n.t('printSummary.title', { petName: petNameSafe })}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;background:white;padding:28px 32px;font-size:14px;line-height:1.5;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    @media print{body{padding:18px 22px}}
    .sec{border:1px solid #e0e0e0;border-radius:8px;padding:16px 18px;margin-bottom:14px;break-inside:avoid}
    .sec-hd{font-size:9px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:#777;margin-bottom:12px}
  </style>
</head>
<body>

  <!-- HEADER -->
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
    <div style="display:flex;align-items:center;gap:10px">
      ${GLYPH_LG}
      <div>
        <div style="font-size:15px;font-weight:700;letter-spacing:2px;line-height:1.1">Cairn</div>
        <div style="font-size:8px;letter-spacing:2px;color:#aaa;text-transform:uppercase;margin-top:1px">PET HEALTH</div>
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:9px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:#666">${i18n.t('printSummary.patientSummary')}</div>
      <div style="font-size:10px;color:#aaa;margin-top:2px">${i18n.t('printSummary.generated', { date: generatedDate })}</div>
    </div>
  </div>

  <!-- PATIENT CARD + MEDICATIONS (two-column) -->
  <div class="sec" style="display:flex;gap:20px;align-items:flex-start">
    <!-- Left: pet identity -->
    <div style="flex:1;min-width:0">
      <div style="font-size:26px;font-weight:700;letter-spacing:-0.5px;margin-bottom:3px">${petNameSafe}</div>
      <div style="font-size:12px;color:#555;margin-bottom:4px">
        ${petIdentityLineSafe}${petAge ? ` · ${petAge}` : ''}
      </div>
      ${dobStr ? `<div style="font-size:11px;color:#888;margin-bottom:3px">${i18n.t('printSummary.dob', { date: dobStr })}</div>` : ''}
      <div style="font-size:11px;color:#777;margin-top:6px">
        <span style="font-weight:600">${i18n.t('printSummary.monitoring')}</span>
        ${conditions.map(c => conditionLabel(c.condition_type)).join(', ')}
      </div>
      ${owner ? `<div style="font-size:10px;color:#aaa;margin-top:4px">${i18n.t('printSummary.owner', { name: ownerNameSafe })}</div>` : ''}
    </div>
    <!-- Right: medications -->
    <div style="width:220px;flex-shrink:0;border-left:1px solid #eee;padding-left:18px">
      <div style="font-size:9px;font-weight:700;letter-spacing:1.2px;color:#777;text-transform:uppercase;margin-bottom:10px">${i18n.t('patientDetail.medications')}</div>
      ${medLines}
      <div style="font-size:9px;color:#bbb;margin-top:10px;line-height:1.4">${i18n.t('printSummary.medicationsDisclaimer')}</div>
    </div>
  </div>

  <!-- CHECK-IN HISTORY -->
  <div class="sec">
    <div class="sec-hd">${i18n.t('printSummary.checkInHistory')}</div>
    ${condSections}
  </div>

  <!-- OPTIONAL TRACKERS (weight · wellbeing · seizures) -->
  ${trackerRow}

  <!-- FOOTER -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-top:12px;border-top:1px solid #e0e0e0">
    <div style="display:flex;align-items:center;gap:8px">
      ${GLYPH_SM}
      <div style="font-size:9px;color:#aaa;line-height:1.5">${i18n.t('printSummary.footerTagline')}</div>
    </div>
    <div style="font-size:9px;color:#aaa;text-align:right;max-width:260px;line-height:1.5">${i18n.t('printSummary.footerDisclaimer')}</div>
  </div>

</body>
</html>`;
}
